/** @jsx jsx */
import { jsx } from '@emotion/core'
import { ListView } from 'antd-mobile'
import { observer } from 'mobx-react'
import React, { Fragment, useEffect, useRef } from 'react'
import { isMobile } from 'react-device-detect'
import * as _ from 'lodash'
import { TournamentTrack } from '@store/matchupStore'
import MatchBlock from '@views/Matchup/MatchField/PreMatchPage/MatchBlock'
import PageLoading from '@components/PageLoading'
import useRootStore from '@store/useRootStore'
import { AdvEmpty } from '@components/AdvEmpty'
import { useOnMount, useOnUnmount } from '@utils/reactExt'
import { GAME_TYPE } from '@game-type/game-market-types'
import { PAGE_TYPE, SPORTS_TYPE } from '@interface/common'
import { EMPTY_ICON } from '@constants/index'
import { Schema$MatchTrack } from '@protocol/game'
import { BLOCK_TYPE } from '@views/Matchup/MatchField/PreMatchPage/MatchBlock/OddsBlock'
import BottomLoading from '@components/BottomLoading'
import CompanyInfoImg from '@assets/images/bg/company-info-pc.png'
import { CompanyRuleWrap, TermsWrap } from '@styles/base.style'
import { Button } from '@styles/button.style'
import { appScrollTo, getAppScrollTop, isLsportsEnv } from '@utils/utils'
import { EXCEPTIONAL_UNIQUE_ID } from '@v2/constants/common'
// img
import MobileCompanyRuleImg from '@assets/images/bg/company-rule/home-footer.png'
import { makeSelectionValue } from '@game/util'

const dataSource = new ListView.DataSource({
  rowHasChanged: (row1, row2) => {
    return row1 !== row2
  },
})

const PAGE_SIZE = 20

interface PreMatchListProps {
  pageType: PAGE_TYPE
  tracks: TournamentTrack[]
  onFieldClickCheck: () => boolean
}

function MatchupCustomBody(props: any) {
  const { children } = props
  return (
    <div className="am-list-body my-body">{children}</div>
  )
}

const PreMatchList: React.FC<PreMatchListProps> = (props: PreMatchListProps) => {
  const {
    matchupStore,
    oddsStore,
    globalStore,
    authStore,
    routerStore,
    companyPickStore,
    userPickStore,
    componentStore,
    logStore,
  } = useRootStore()
  const { isGoToScrollTopOfMatchList } = componentStore
  const {
    // tracks,
    isLoading,
    currentGameKey,
    viewControllers,
    currentSportsKey,
    currentSportId,
    cartUpdated,
    enabledClubLiveLicense,
    hasMore,
    majorUniqueID,
  } = matchupStore
  const { onFieldClickCheck, pageType, tracks } = props

  const gameKey = (pageType === PAGE_TYPE.MATCHUP_RESULT || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER)
    ? GAME_TYPE.CROSS : currentGameKey

  useOnMount(() => {
    if (pageType !== PAGE_TYPE.MATCHUP_RESULT && gameKey === GAME_TYPE.LIVE) {
      oddsStore.enter('unified')
    }

    if (pageType !== PAGE_TYPE.MATCHUP_FILTER
      && pageType !== PAGE_TYPE.MATCHUP_RESULT_FILTER) {
      setTimeout(() => {
        appScrollTo(1)
      }, 100)
    }

    if (isUseBodyScroll()) {
      window.removeDuplicateAntMobileListViewScrollListeners(true)
    }
  })

  useOnUnmount(() => {
    oddsStore.cleanup()
    // ant-mobile ListView 에서 componentWillUnMount 발생 후 componentDidUpdate 가 호출되는 경우
    // setTimeout( () => {}, 0) 을 이용해 이벤트를 등록하기 때문에 이벤트 해제 시점을 조금 늦춰야 한다.
    if (isUseBodyScroll()) {
      setTimeout(() => {
        window.removeDuplicateAntMobileListViewScrollListeners(false)
      }, 10)
    }
  })

  const getMatchInTrackByMatchId = matchId => {
    let match: Schema$MatchTrack
    for (const track of tracks) {
      match = track.matches.find(object => object.matchId === matchId)
      if (match) {
        break
      }
    }
    return match
  }

  const getMarketInMatchByMarketType = (match, marketType) => {
    let result = []
    if (match) {
      const regular = match.regulars.find(object => object.type === marketType)
      if (regular && regular.markets) {
        result = regular.markets
      }
    }
    return result
  }

  const getSpecialBetValue = (oddsBlockType, specifiers) => {
    let specialBetValue = null
    if (oddsBlockType === BLOCK_TYPE.HANDICAP) {
      specialBetValue = _.replace(specifiers.hcp, ' (0-0)', '')
    } else if (oddsBlockType === BLOCK_TYPE.UNDEROVER) {
      specialBetValue = specifiers.total
    }
    return specialBetValue
  }

  const restoreOddsButtonGroupStateFromCart = () => {
    const { cart } = matchupStore

    if (cart.length === 0) {
      clearFieldAndEnableByAll()
      return
    }

    for (const cartItem of cart) {
      const {
        matchId,
        marketType,
        sportId,
        selection,
        selectedOutcome,
        value,
        producerId,
        isLocked,
      } = cartItem

      const viewGroup = viewControllers[matchId]
      if (!viewGroup) {
        continue
      }

      const specifiers = JSON.stringify(cartItem.specifiers)

      if (gameKey === GAME_TYPE.LIVE) {
        clearFieldByMatchIdWithoutMarketType(matchId, marketType)
      } else if (gameKey === GAME_TYPE.CROSS) {
        if (!matchupStore.isMainMarket(marketType)) {
          clearFieldByMatchIdWithoutMarketType(matchId, marketType)
        } else {
          matchupStore.updateFightingOdds(matchId, marketType, false)
          matchupStore.updateDisabledOdds(matchId, marketType, sportId, value)
        }
      }

      const view = viewGroup[marketType]
      if (!view) {
        continue
      }

      if (isLocked) {
        view.clearField()
        continue
      }

      const match = getMatchInTrackByMatchId(matchId)
      if (!match) {
        continue
      }

      const oddsBlockType = view.oddsBlockType
      const specialBetValue = getSpecialBetValue(oddsBlockType, cartItem.specifiers)

      const marketList = getMarketInMatchByMarketType(match, marketType)

      let market = null
      if (specialBetValue) {
        market = marketList.find(object => {
          return object.type === Number(marketType) && object.specialBetValue === specialBetValue
        })
      } else {
        market = marketList.find(object => {
          return object.type === Number(marketType)
        })
      }
      if (!market) {
        continue
      }

      const outcome = market.outcomes.find(object => object.type === selectedOutcome.type)
      const rate = outcome.rate
      const metaId = isLsportsEnv() ? outcome.metaId : selection
      const newValue = makeSelectionValue({
        marketType,
        specifiers,
        outcomeType: selection,
        rate,
        outcomeMetaId: metaId,
        key: selection,
        producerId,
      })

      view.restoreOddsButtonGroupStateForCallFromParent(oddsBlockType, newValue, marketList, marketType, sportId)
    }
  }

  const clearFieldAndEnableByAll = () => {
    for (const vKey in viewControllers) {
      const viewGroup = viewControllers[vKey]
      if (viewGroup) {
        for (const key in viewGroup) {
          if (Object.prototype.hasOwnProperty.call(viewGroup, key)) {
            const view = viewGroup[key]
            view.setEnabled(true)
            view.setEnabledAway(true)
            view.setEnabledDraw(true)
            view.setEnabledHome(true)
            view.clearField()
          }
        }
      }
    }
  }

  const clearFieldByMatchIdWithoutMarketType = (matchId: number, marketType: string) => {
    const viewGroup = viewControllers[matchId]
    for (const key in viewGroup) {
      if (key !== marketType) {
        if (Object.prototype.hasOwnProperty.call(viewGroup, key)) {
          const view = viewGroup[key]
          view.clearField()
        }
      }
    }
  }

  useEffect(() => {
    // NOTE : 카테고리 이동시 cart에 담긴 데이터로 프론트 매치업리스트의 버튼상태를 관리하기위한 func
    //        자식이 다 그려진 상태에서 유효한데 스크롤시 부모가 먼저 불리고 그 후에 자식이 세팅되기때문에
    //        setTimeOut으로 자식이 세팅된 후에 자식들의 버튼을 관리하도록 수정
    if (!_.isNil(cartUpdated)) {
      setTimeout(() => {
        restoreOddsButtonGroupStateFromCart()
      }, 0)
    }
  })

  const onEndReached = async event => {
    if (isLoading) {
      return
    }

    if (gameKey === GAME_TYPE.LIVE) {
      return
    }

    if (_.isEmpty(tracks)) {
      return
    }

    if (pageType === PAGE_TYPE.MATCHUP_FILTER || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER) {
      await matchupStore.fetchFilteredTournamentTracks({ limit: PAGE_SIZE, reset: false, pageType })
    } else {
      await matchupStore.fetchTournamentTracks({ limit: PAGE_SIZE, reset: false, pageType })
    }

    if (isMobile) {
      setTimeout(() => {
        const scrollTop = getAppScrollTop()
        appScrollTo(scrollTop + 1)
      }, 100)
    }
  }

  const minimumLiveMatchOutcomeRate = globalStore.getMinimumLiveMatchOutcomeRate()

  const isTotalLeague = matchupStore.majorUniqueID === EXCEPTIONAL_UNIQUE_ID.TOTAL_LEAGUE
  const isMajorLeague = matchupStore.majorUniqueID === EXCEPTIONAL_UNIQUE_ID.MAJOR_LEAGUE
  const isFavoriteMatch = matchupStore.majorUniqueID === EXCEPTIONAL_UNIQUE_ID.FAVORITE_MATCH
  const isMyBetMatch = matchupStore.majorUniqueID === EXCEPTIONAL_UNIQUE_ID.MY_BET_MATCH
  const isLive = matchupStore.currentGameKey === GAME_TYPE.LIVE
  const visibleBanner = !isLive && (isTotalLeague || isMajorLeague || isFavoriteMatch || isMyBetMatch)

  const row = (rowData: TournamentTrack, sectionID, rowID) => {
    const ukey = `PreMatchList_MatchBlock_${rowData.id}`

    return (
      <MatchBlock
        rowIndex={rowID}
        visibleBanner={visibleBanner}
        pageType={pageType}
        key={ukey}
        gameType={gameKey}
        track={rowData}
        matchupStore={matchupStore}
        globalStore={globalStore}
        authStore={authStore}
        routerStore={routerStore}
        companyPickStore={companyPickStore}
        userPickStore={userPickStore}
        logStore={logStore}
        onFieldClickCheck={onFieldClickCheck}
        minimumLiveMatchOutcomeRate={minimumLiveMatchOutcomeRate}
      />
    )
  }

  // 약관 이동
  let url
  const mobileURL = `${process.env.HOMEPAGE_MO}/#/cs_policy`
  const list = [
    {
      key: 'terms_use',
      name: '이용약관',
      onClickQuickMenu: () => {
        if (isMobile) {
          url = mobileURL
        } else {
          url = `${process.env.HOMEPAGE_PC}/#/cs/terms/terms_use`
        }
        globalStore.toMoveWebHomePage(url)
      },
    },
    {
      key: 'terms_mobile',
      name: '모바일 이용약관',
      onClickQuickMenu: () => {
        if (isMobile) {
          url = mobileURL
        } else {
          url = `${process.env.HOMEPAGE_PC}/#/cs/terms/terms_mobile`
        }
        globalStore.toMoveWebHomePage(url)
      },
    },
    {
      key: 'terms_private',
      name: '개인정보처리방침',
      onClickQuickMenu: () => {
        if (isMobile) {
          url = mobileURL
        } else {
          url = `${process.env.HOMEPAGE_PC}/#/cs/terms/terms_private`
        }
        globalStore.toMoveWebHomePage(url)
      },
    },
    {
      key: 'terms_kid',
      name: '청소년보호정책',
      onClickQuickMenu: () => {
        if (isMobile) {
          url = mobileURL
        } else {
          url = `${process.env.HOMEPAGE_PC}/#/cs/terms/terms_kid`
        }
        globalStore.toMoveWebHomePage(url)
      },
    },
    {
      key: 'terms_operate',
      name: '운영정책',
      onClickQuickMenu: () => {
        if (isMobile) {
          url = mobileURL
        } else {
          url = `${process.env.HOMEPAGE_PC}/#/cs/terms/terms_operate`
        }
        globalStore.toMoveWebHomePage(url)
      },
    },
    {
      key: 'terms_cash',
      name: 'S캐시정책',
      onClickQuickMenu: () => {
        if (isMobile) {
          url = mobileURL
        } else {
          url = `${process.env.HOMEPAGE_PC}/#/cs/terms/terms_cash`
        }
        globalStore.toMoveWebHomePage(url)
      },
    },
  ]

  const renderFooter = () => {
    let footer
    if (isLoading) {
      footer = <PageLoading/>
    } else if (gameKey === GAME_TYPE.LIVE
      && !enabledClubLiveLicense) {
      footer = <AdvEmpty ico={EMPTY_ICON.LIVE_DISABLED}/>
    } else if (tracks.length > 0) {

      const isVisibleCompanyRule = isMobile
        && hasMore === false
        && gameKey === GAME_TYPE.CROSS
        && Number(currentSportId) === -1
        && majorUniqueID === EXCEPTIONAL_UNIQUE_ID.MAJOR_LEAGUE
        && pageType !== PAGE_TYPE.MATCHUP_FILTER
        && pageType !== PAGE_TYPE.MATCHUP_RESULT_FILTER
        && currentSportsKey === SPORTS_TYPE.TODAY

      const isVisiblePCTerms = !isMobile
        && hasMore === false
        && gameKey === GAME_TYPE.CROSS
        && Number(currentSportId) === -1
        && majorUniqueID === EXCEPTIONAL_UNIQUE_ID.MAJOR_LEAGUE
        && pageType !== PAGE_TYPE.MATCHUP_FILTER
        && pageType !== PAGE_TYPE.MATCHUP_RESULT_FILTER
        && currentSportsKey === SPORTS_TYPE.TODAY

      if (gameKey === GAME_TYPE.LIVE) {
        footer = <Fragment/>
      }

      if (isVisibleCompanyRule) {
        // 등급분류 이미지 (스포츠 - 전체 - 오늘의 주요경기) 탭에서만 호출되어야 합니다.
        footer = (
          <Fragment>
            <TermsWrap>
              <ul className="terms-list">
                {list.map((listItem, index) =>
                  <li key={listItem.key} className="terms-list--item">
                    <Button TP className="link-btn" onClick={listItem.onClickQuickMenu}>{listItem.name}</Button>
                  </li>,
                )}
              </ul>
            </TermsWrap>
            <CompanyRuleWrap>
              <img src={MobileCompanyRuleImg} alt="등급분류 이미지" style={{ width: '100%' }}/>
            </CompanyRuleWrap>
          </Fragment>
        )
      } else if (isVisiblePCTerms) {
        footer = (
          <Fragment>
            <TermsWrap className={!isMobile && 'terms-wrap-pc'}>
              <ul className="terms-list">
                {list.map((listItem, index) =>
                  <li key={listItem.key} className="terms-list--item">
                    <Button TP className="link-btn" onClick={listItem.onClickQuickMenu}>{listItem.name}</Button>
                  </li>,
                )}
              </ul>
            </TermsWrap>
            <CompanyRuleWrap>
              <img src={CompanyInfoImg} alt="회사정보" style={{ width: '100%' }}/>
            </CompanyRuleWrap>
          </Fragment>
        )
      }

      if (!isVisibleCompanyRule
        && !isVisiblePCTerms
        && gameKey !== GAME_TYPE.LIVE
        && hasMore) {
        footer = (<BottomLoading/>)
      }

      if (!isVisibleCompanyRule
        && !isVisiblePCTerms
        && gameKey !== GAME_TYPE.LIVE
        && !hasMore) {
        footer = (<Fragment/>)
      }

    } else {
      let emptyIco
      if (pageType === PAGE_TYPE.MATCHUP_RESULT) {
        if (majorUniqueID === EXCEPTIONAL_UNIQUE_ID.MY_BET_MATCH){
          emptyIco = EMPTY_ICON.MATCH_RESULT_MYBET
        }else{
          emptyIco = EMPTY_ICON.MATCH_RESULT
        }
      } else if (gameKey === GAME_TYPE.LIVE) {
        if (majorUniqueID === EXCEPTIONAL_UNIQUE_ID.MY_BET_MATCH){
          emptyIco = EMPTY_ICON.LIVE_MYBET
        }else{
          emptyIco = EMPTY_ICON.LIVE
        }
      } else {
        emptyIco = EMPTY_ICON.MATCH
      }
      footer = <AdvEmpty ico={emptyIco}/>
    }
    return <div css={{ textAlign: 'center' }}>{footer}</div>
  }

  const isUseBodyScroll = () => {
    return isMobile && !isMatchFilter
  }

  const listViewKey = 'PreMatchList:ListView'
  const isMatchFilter = (pageType === PAGE_TYPE.MATCHUP_FILTER || pageType === PAGE_TYPE.MATCHUP_RESULT_FILTER)

  const listViewProps = {
    key: listViewKey,
    css: { width: '100%', marginTop: pageType === PAGE_TYPE.MATCHUP_RESULT ? 0 : 9 },
    dataSource: dataSource.cloneWithRows(tracks),
    renderFooter,
    renderRow: row,
    className: 'am-list',
    initialListSize: PAGE_SIZE,
    pageSize: PAGE_SIZE,
    useBodyScroll: isUseBodyScroll(),
    renderBodyComponent: () => <MatchupCustomBody/>,
    scrollRenderAheadDistance: 500,
    onEndReached,
    onEndReachedThreshold: 10,
  }

  if (isMobile) {
    delete listViewProps.renderBodyComponent
  }

  const matchListRefs = useRef(null)
  useEffect(() => {
    if (matchListRefs && isGoToScrollTopOfMatchList) {
      setTimeout(() => {
        appScrollTo(1)
      }, 100)
    }

    componentStore.setIsGoToScrollTopOfMatchList(false)
  }, [isGoToScrollTopOfMatchList])

  return (
    <ListView  {...listViewProps} ref={matchListRefs}/>
  )
}

export default observer(PreMatchList)
